import {
  pageDisplayName,
  type BoxConfiguration,
  type DocumentCounts,
  type FieldType,
  type SinglePage,
  type Template,
  type TemplateField,
  type TextType,
} from '@/types/domain'

/**
 * Everything the Vorlage tab derives from a template, with no React in it.
 *
 * The screen is mostly arithmetic over two structures the gateway does not
 * join for us — the page list (layouts and their variants) and the shared
 * field set (one row per export column) — and every one of those sums is a
 * sentence a person reads as fact. They are here so a wrong one shows up in a
 * test rather than in a support ticket.
 */

// -- Layouts ---------------------------------------------------------------

export interface LayoutEntry {
  page: SinglePage
  /** The primary page this one is an alternative layout for, if any. */
  parent?: SinglePage
  /** Documents the matcher assigned to this page. */
  documents: number
  /** Page 1 and the last remaining page cannot be deleted (400
   *  `page_immutable`), so the action is not offered for them. */
  removable: boolean
}

/**
 * Documents matched to one template page.
 *
 * `byMatchedPage` is keyed by the page *number* as a string and omits
 * `noMatch` documents — a page missing from it has none, which is 0 and not
 * "unknown". Absent counts (the request has not landed yet) also read as 0;
 * the card says "0 Dokumente" for a moment rather than flickering a number.
 */
export function matchedDocuments(counts: DocumentCounts | undefined, page: number): number {
  return counts?.byMatchedPage?.[String(page)] ?? 0
}

/**
 * The cards, in reading order: each primary page followed by its own
 * variants.
 *
 * Page numbers are never compacted — a deleted page leaves a gap and the
 * number is not a position — so ordering is by `page` ascending within each
 * group and never by index. A variant whose parent is missing (it should not
 * happen: deleting a page reparents its variants) is still listed, at the
 * end, rather than dropped: a layout the export uses must never be invisible
 * here.
 */
export function layoutEntries(
  template: Template | undefined,
  counts?: DocumentCounts,
): LayoutEntry[] {
  const pages = [...(template?.pageList ?? [])].sort((a, b) => a.page - b.page)
  if (pages.length === 0) return []
  const byNumber = new Map(pages.map((page) => [page.page, page]))
  const removable = (page: SinglePage) => page.page !== 1 && pages.length > 1

  const entries: LayoutEntry[] = []
  const placed = new Set<number>()

  for (const page of pages) {
    if (page.variantOf != null) continue
    entries.push({ page, documents: matchedDocuments(counts, page.page), removable: removable(page) })
    placed.add(page.page)
    for (const variant of pages) {
      if (variant.variantOf !== page.page) continue
      entries.push({
        page: variant,
        parent: page,
        documents: matchedDocuments(counts, variant.page),
        removable: removable(variant),
      })
      placed.add(variant.page)
    }
  }

  for (const page of pages) {
    if (placed.has(page.page)) continue
    entries.push({
      page,
      parent: page.variantOf != null ? byNumber.get(page.variantOf) : undefined,
      documents: matchedDocuments(counts, page.page),
      removable: removable(page),
    })
  }

  return entries
}

/** The primary pages a new variant may be an alternative to. `variantOf` is
 *  one level deep, so a variant can never be the target. */
export function variantTargets(template: Template | undefined): SinglePage[] {
  return [...(template?.pageList ?? [])]
    .filter((page) => page.variantOf == null)
    .sort((a, b) => a.page - b.page)
}

export function documentsLabel(count: number): string {
  return count === 1 ? '1 Dokument' : `${count} Dokumente`
}

/**
 * The line under a layout's name.
 *
 * A variant is described by what it is an alternative *to*, not by its own
 * page number: "Seite 7" on a card called "2019" is a database fact that
 * answers no question a person has. The artboard shows "Seite 1" on both
 * cards, which would be wrong on the variant — its page number is its own.
 */
export function layoutMeta(entry: LayoutEntry): string {
  const documents = documentsLabel(entry.documents)
  if (entry.page.variantOf != null) return `Alternative zu Seite ${entry.page.variantOf} · ${documents}`
  return `Seite ${entry.page.page} · ${documents}`
}

export function layoutName(page: SinglePage): string {
  return pageDisplayName(page)
}

// -- Fields ----------------------------------------------------------------

/** `text` (client-authored) and `textDetection` (server/legacy) are the same
 *  family — `typeFamily` in the gateway's `internal/templates/fields.go`. */
export function isTextField(type: string): boolean {
  return type === 'text' || type === 'textDetection'
}

export function fieldTypeLabel(type: string): string {
  if (isTextField(type)) return 'Text'
  if (type === 'checkBox') return 'Checkbox'
  if (type === 'radioBox') return 'Auswahl'
  if (type === 'signature') return 'Signatur'
  return type
}

/** The design system fixes these three glyphs; `radioBox` shares the
 *  checkbox's, being the same kind of mark on paper. */
export function fieldTypeIcon(type: string): string {
  if (type === 'signature') return 'signature'
  if (type === 'checkBox' || type === 'radioBox') return 'disabled_by_default'
  return 'stylus_note'
}

const TEXT_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  date: 'Datum',
  number: 'Zahl',
  email: 'E-Mail',
  phone: 'Telefon',
  iban: 'IBAN',
  postcode: 'PLZ',
  url: 'Web-Adresse',
  id: 'Nummer',
}

/** The "Inhalt" column: what kind of text the field holds. Only text fields
 *  have one — a checkbox is ticked or not. */
export function contentLabel(type: string, textType?: TextType | string): string {
  if (!isTextField(type)) return '–'
  if (!textType) return 'Text'
  return TEXT_TYPE_LABELS[textType] ?? 'Text'
}

export interface FieldRow {
  id: string
  name: string
  type: FieldType | string
  textType?: TextType | string
  ordinal: number
  /** Page numbers this field has a box on. */
  placedOn: number[]
  /** False for a template with no shared field set, where the row is a
   *  pseudo-field grouped by label and has no id the gateway would accept. */
  editable: boolean
}

/**
 * Which pages each field is placed on, read off the boxes.
 *
 * Keyed by `fieldId` where the template has one. Older and Firestore
 * templates carry none, and the gateway's own `/fields` falls back to
 * grouping by label there — this does the same, so the matrix says the same
 * thing the export will do.
 */
export function fieldPlacement(template: Template | undefined): Map<string, Set<number>> {
  const placement = new Map<string, Set<number>>()
  const add = (key: string, page: number) => {
    const pages = placement.get(key) ?? new Set<number>()
    pages.add(page)
    placement.set(key, pages)
  }
  for (const page of template?.pageList ?? []) {
    for (const box of page.boxConfiguration ?? []) {
      add(box.fieldId ?? labelKey(box), page.page)
    }
  }
  return placement
}

function labelKey(box: BoxConfiguration): string {
  return `label:${box.label}`
}

/**
 * One row per export column, in export order.
 *
 * `fields` present is authoritative — it is what the gateway keys the xlsx
 * columns by. Without it (Firestore, or a template older than migration
 * 00006) the rows are pseudo-fields grouped by label, marked `editable:
 * false`: they have no id, so neither reordering nor deleting them is
 * something the gateway could carry out, and offering either would be a
 * control that silently does nothing.
 */
export function fieldRows(template: Template | undefined): FieldRow[] {
  const placement = fieldPlacement(template)
  const fields = template?.fields ?? []

  if (fields.length > 0) {
    return [...fields]
      .sort((a, b) => a.ordinal - b.ordinal || a.name.localeCompare(b.name, 'de'))
      .map((field) => ({
        id: field.id,
        name: field.name,
        type: field.type,
        textType: field.textType,
        ordinal: field.ordinal,
        placedOn: [...(placement.get(field.id) ?? [])].sort((a, b) => a - b),
        editable: true,
      }))
  }

  const seen = new Map<string, FieldRow>()
  for (const page of template?.pageList ?? []) {
    for (const box of page.boxConfiguration ?? []) {
      const key = labelKey(box)
      const existing = seen.get(key)
      if (existing) {
        if (!existing.placedOn.includes(page.page)) existing.placedOn.push(page.page)
        continue
      }
      seen.set(key, {
        id: key,
        name: box.label,
        type: box.type,
        textType: box.textType,
        ordinal: seen.size,
        placedOn: [page.page],
        editable: false,
      })
    }
  }
  return [...seen.values()].map((row) => ({ ...row, placedOn: [...row.placedOn].sort((a, b) => a - b) }))
}

/**
 * The field set with one row moved one place, renumbered 0..n-1.
 *
 * Ordinals are renumbered rather than swapped so a template whose ordinals
 * have drifted (duplicates after a migration, gaps after a delete) comes out
 * of any reorder in a consistent state.
 */
export function reorderFields(
  fields: TemplateField[],
  fieldId: string,
  direction: 'up' | 'down',
): TemplateField[] {
  const ordered = [...fields].sort((a, b) => a.ordinal - b.ordinal || a.name.localeCompare(b.name, 'de'))
  const from = ordered.findIndex((field) => field.id === fieldId)
  if (from < 0) return ordered.map(renumber)
  const to = direction === 'up' ? from - 1 : from + 1
  if (to < 0 || to >= ordered.length) return ordered.map(renumber)
  const moved = [...ordered]
  const [field] = moved.splice(from, 1)
  moved.splice(to, 0, field)
  return moved.map(renumber)
}

function renumber(field: TemplateField, index: number): TemplateField {
  return { ...field, ordinal: index }
}

/**
 * Whether two field sets describe the same fields.
 *
 * `PATCH /v1/templates/:id` with `fields` is authoritative and has no
 * conflict detection: sending a set that was read before somebody else added
 * a field would drop theirs without a word. Every write here re-reads the
 * template first and compares with this; a difference stops the write and
 * says so instead of overwriting.
 */
export function sameFieldSet(a: TemplateField[], b: TemplateField[]): boolean {
  if (a.length !== b.length) return false
  const ids = new Set(a.map((field) => field.id))
  return b.every((field) => ids.has(field.id))
}

/** Where the box editor opens for one layout. Page *number*, not index. */
export function editorPath(templateId: string, page: number, fieldId?: string): string {
  const base = `/templates/${templateId}/bearbeiten/${page}`
  return fieldId ? `${base}?feld=${encodeURIComponent(fieldId)}` : base
}
